import React, { useRef } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import MarkInput from "./MarkInput";

export default function QuestionIndex({
  paper,
  marks = {},
  work = {},
  activeKey,
  pagesByQuestion = {},
  readOnly,
  onActivate,
  onJumpToPage,
  onMark,
}) {
  const inputs = useRef({});

  if (!paper?.questions?.length) {
    return (
      <Box p={2}>
        <Typography color="error" variant="body2">
          No questions found. Check the paper scheme.
        </Typography>
      </Box>
    );
  }

  const focusNext = (index) => {
    const next = paper.questions[index + 1];
    if (!next) return;
    onActivate(next.key, false);
    inputs.current[next.key]?.focus();
  };

  return (
    <Box component="ul" sx={{ m: 0, p: 1, listStyle: "none" }}>
      {paper.questions.map((question, index) => {
        const isActive = question.key === activeKey;
        const isMarked =
          marks[question.key] !== null && marks[question.key] !== undefined;
        const pages = pagesByQuestion[question.key] || [];

        return (
          <Box
            component="li"
            key={question.key}
            onClick={() => onActivate(question.key, true)}
            sx={{
              mb: 0.5,
              px: 1,
              py: 0.75,
              borderRadius: 1.5,
              cursor: "pointer",
              border: "1px solid",
              borderColor: isActive ? "primary.main" : "transparent",
              bgcolor: isActive ? "#edf4ff" : "transparent",
              "&:hover": { bgcolor: isActive ? "#edf4ff" : "#f5f8fc" },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip
                arrow
                placement="right"
                title={question.title || ""}
                enterDelay={200}
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 320, fontSize: 13, lineHeight: 1.45 },
                  },
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  flex={1}
                  minWidth={0}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: isMarked ? "success.main" : "#c5cfdd",
                    }}
                  />
                  <Typography variant="body2" fontWeight={700} noWrap>
                    Q{question.no}
                  </Typography>
                </Box>
              </Tooltip>

              <Box onClick={(event) => event.stopPropagation()}>
                <MarkInput
                  max={question.maxMarks}
                  value={marks[question.key]}
                  work={work[question.key]}
                  disabled={readOnly}
                  inputRef={(node) => {
                    inputs.current[question.key] = node;
                  }}
                  onChange={(value) => onMark(question.key, value)}
                  onFocus={() => onActivate(question.key, false)}
                  onNext={() => focusNext(index)}
                />
              </Box>
            </Box>

            {pages.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.75} pl={2}>
                {pages.map((page) => (
                  <Chip
                    key={page}
                    size="small"
                    label={`p${page}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onActivate(question.key, false);
                      onJumpToPage(page);
                    }}
                    sx={{
                      height: 20,
                      fontSize: 11,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
